import { memo } from 'react';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { safeFormatTime } from '../../../shared/utils/dateUtils';
import { useSpokeActivity } from '../hooks/useSpokeActivity';
import type { Deployment } from '../../../types/backend';

interface SpokeActivityViewProps {
  customerId: string;
  deployments: Deployment[];
  active: boolean;
}

const SOURCE_COLOR: Record<string, 'success' | 'info' | 'secondary' | 'warning' | 'default'> = {
  heartbeat: 'success',
  lifecycle: 'info',
  admin: 'secondary',
  system: 'warning',
};

function dayLabel(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

const SpokeActivityView = memo<SpokeActivityViewProps>(({ customerId, deployments, active }) => {
  const { days, total, loading, error } = useSpokeActivity(customerId, deployments, active);

  if (loading) {
    return <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Box sx={{ p: 3 }}><Typography color="error">{error}</Typography></Box>;
  }
  if (total === 0) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No activity recorded for this spoke.</Typography></Box>;
  }

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
      {days.map((day) => (
        <Box key={day.date} sx={{ mb: 2 }}>
          <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', py: 0.5, mb: 0.5, zIndex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
              {dayLabel(day.date)} · {day.entries.length} event{day.entries.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
            {day.entries.map((e) => (
              <Box key={e.event_id} sx={{ display: 'grid', gridTemplateColumns: (theme) => `${theme.spacing(8)} 1fr`, gap: 1.5, py: 1 }}>
                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', textAlign: 'right', pt: 0.25 }}>
                  {safeFormatTime(e.timestamp, { hour: '2-digit', minute: '2-digit' }, '—')}
                </Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.event_type}</Typography>
                    <Chip size="small" variant="outlined" label={e.product} />
                    <Chip size="small" color={SOURCE_COLOR[e.source] ?? 'default'} variant="outlined" label={e.source} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {[e.version, e.status, e.action, e.result, e.actor].filter(Boolean).join(' · ') || '—'}
                  </Typography>
                  {e.error_detail && (
                    <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>{e.error_detail}</Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
});

SpokeActivityView.displayName = 'SpokeActivityView';
export default SpokeActivityView;
